import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'

import NavBar from '../components/layout/NavBar'
import BookingCalendar from '../components/booking/BookingCalendar'
import BookingDaySlots from '../components/booking/BookingDaySlots'
import { addDaysIso, monthStartIso, todayIsoDate } from '../lib/bookingCalendar'
import {
  bookingAvailability,
  cancelBookingWithReason,
  createBookingReview,
  downloadBookingIcs,
  getBookingDetail,
  getBookingReview,
  payBookingNow,
  refundBooking,
  resendBookingConfirm,
  rescheduleBooking,
} from '../api/bookings'
import type { BookingDetailResponse, BookingReview, BookingSlot } from '../types/bookings'

const stripePublicKey = (import.meta as any).env?.VITE_STRIPE_PUBLISHABLE_KEY || ''
const stripePromise = stripePublicKey ? loadStripe(stripePublicKey) : null

function BookingPaymentConfirm({
  onSuccess,
  onError,
  busy,
  setBusy,
}: {
  onSuccess: () => void
  onError: (message: string) => void
  busy: boolean
  setBusy: (value: boolean) => void
}) {
  const stripe = useStripe()
  const elements = useElements()

  const onConfirm = async () => {
    if (!stripe || !elements || busy) return
    setBusy(true)
    onError('')
    const result = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    })
    if (result.error) {
      onError(result.error.message || 'Payment confirmation failed.')
      setBusy(false)
      return
    }
    setBusy(false)
    onSuccess()
  }

  return (
    <div className="space-y-3">
      <PaymentElement />
      <button
        type="button"
        onClick={onConfirm}
        disabled={busy || !stripe || !elements}
        className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
      >
        {busy ? 'Processing...' : 'Pay now'}
      </button>
    </div>
  )
}

function fmtWhen(iso: string, timeZone?: string): string {
  try {
    return new Date(iso).toLocaleString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timeZone || 'UTC',
    })
  } catch {
    return iso
  }
}

function money(cents: number | null | undefined): string {
  if (cents == null) return '—'
  return `$${(Number(cents || 0) / 100).toFixed(2)}`
}

export default function BookingDetailPage() {
  const { publicId = '' } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const tokenInUrl = String(searchParams.get('t') || '').trim()
  const [guestToken] = useState<string>(() => tokenInUrl)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [detail, setDetail] = useState<BookingDetailResponse | null>(null)
  const [actionBusy, setActionBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  const [rescheduleOpen, setRescheduleOpen] = useState(false)
  const [rescheduleDate, setRescheduleDate] = useState('')
  const [rescheduleMonth, setRescheduleMonth] = useState('')
  const [rescheduleLoading, setRescheduleLoading] = useState(false)
  const [rescheduleError, setRescheduleError] = useState<string | null>(null)
  const [rescheduleSlots, setRescheduleSlots] = useState<BookingSlot[]>([])
  const [rescheduleDaySummaries, setRescheduleDaySummaries] = useState<Record<string, { availableCount: number; enabled: boolean; closed: boolean }>>({})
  const [rescheduleSelected, setRescheduleSelected] = useState<BookingSlot | null>(null)

  const [payOpen, setPayOpen] = useState(false)
  const [payClientSecret, setPayClientSecret] = useState('')
  const [payBusy, setPayBusy] = useState(false)
  const [payError, setPayError] = useState<string | null>(null)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [reviewBusy, setReviewBusy] = useState(false)
  const [reviewError, setReviewError] = useState<string | null>(null)
  const [reviewRating, setReviewRating] = useState(5)
  const [reviewBody, setReviewBody] = useState('')
  const [bookingReview, setBookingReview] = useState<BookingReview | null>(null)

  const loadDetail = useCallback(async () => {
    if (!publicId) return
    setLoading(true)
    setError(null)
    try {
      const row = await getBookingDetail(publicId, guestToken || undefined)
      setDetail(row)
      // Scrub signed token from the address bar after initial auth, but keep it in state
      // so subsequent actions (pay now, refetch) can still use guest access.
      if (tokenInUrl && guestToken) {
        navigate(`/booking/${publicId}`, { replace: true })
      }
    } catch (e: any) {
      const status = Number(e?.response?.status || 0)
      if (status === 401 || status === 403) {
        setError('This link is invalid or expired.')
      } else {
        setError(e?.response?.data?.detail || e?.message || 'Failed to load booking')
      }
    } finally {
      setLoading(false)
    }
  }, [publicId, guestToken, tokenInUrl, navigate])

  useEffect(() => {
    void loadDetail()
  }, [loadDetail])

  const host = detail?.host
  const booking = detail?.booking
  const flags = detail?.flags
  const policy = detail?.policy
  const hostLink = useMemo(() => {
    if (!host) return ''
    if (host.type === 'business') return host.slug ? `/b/${encodeURIComponent(host.slug)}` : ''
    return host.handle ? `/p/${encodeURIComponent(host.handle)}` : ''
  }, [host])

  const statusTone =
    booking?.status === 'CANCELLED'
      ? 'border-red-400/40 bg-red-500/15 text-red-100'
      : 'border-emerald-400/40 bg-emerald-500/15 text-emerald-100'
  const paymentTone =
    booking?.payment_status === 'PAID'
      ? 'border-emerald-400/40 bg-emerald-500/15 text-emerald-100'
      : booking?.payment_status === 'REQUIRES_PAYMENT'
        ? 'border-amber-400/40 bg-amber-500/15 text-amber-100'
        : booking?.payment_status === 'FAILED'
          ? 'border-red-400/40 bg-red-500/15 text-red-100'
          : 'border-white/20 bg-white/10 text-white/80'

  const loadRescheduleDay = useCallback(async (date: string) => {
    if (!detail || !date) return
    setRescheduleLoading(true)
    setRescheduleError(null)
    try {
      const isBusiness = detail.booking.host_type === 'BUSINESS'
      const payload = await bookingAvailability({
        host_type: isBusiness ? 'business' : 'pro',
        slug: isBusiness ? detail.host.slug : undefined,
        handle: !isBusiness ? detail.host.handle : undefined,
        date,
        service_id: detail.booking.service || undefined,
      })
      const slots = Array.isArray(payload.slots) ? payload.slots : []
      setRescheduleSlots(slots)
      setRescheduleDaySummaries((prev) => ({
        ...prev,
        [date]: {
          availableCount: slots.length,
          enabled: Boolean(payload.enabled),
          closed: Boolean(payload.closed),
        },
      }))
    } catch (e: any) {
      setRescheduleSlots([])
      setRescheduleError(e?.response?.data?.detail || e?.message || 'Failed to load availability')
    } finally {
      setRescheduleLoading(false)
    }
  }, [detail])

  useEffect(() => {
    if (!rescheduleOpen || !booking) return
    const initial = booking.starts_at.slice(0, 10)
    setRescheduleDate(initial)
    setRescheduleMonth(monthStartIso(initial || todayIsoDate()))
  }, [rescheduleOpen, booking])

  useEffect(() => {
    if (!rescheduleOpen || !rescheduleDate) return
    void loadRescheduleDay(rescheduleDate)
  }, [rescheduleOpen, rescheduleDate, loadRescheduleDay])

  const onCancel = async () => {
    if (!booking) return
    const reason = window.prompt('Cancel reason (optional):', '') || ''
    setActionBusy(true)
    setMsg(null)
    try {
      await cancelBookingWithReason(booking.public_id, reason.trim())
      setMsg('Booking cancelled.')
      await loadDetail()
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || e?.message || 'Failed to cancel booking.')
    } finally {
      setActionBusy(false)
    }
  }

  const onRefund = async () => {
    if (!booking) return
    if (!window.confirm('Issue a full refund for this booking deposit?')) return
    setActionBusy(true)
    setMsg(null)
    try {
      await refundBooking(booking.public_id)
      setMsg('Refund requested. Status will update after webhook processing.')
      await loadDetail()
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || e?.message || 'Failed to request refund.')
    } finally {
      setActionBusy(false)
    }
  }

  const onResend = async () => {
    if (!booking) return
    setActionBusy(true)
    setMsg(null)
    try {
      await resendBookingConfirm(booking.public_id)
      setMsg('Confirmation email queued.')
      await loadDetail()
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || e?.message || 'Failed to resend email.')
    } finally {
      setActionBusy(false)
    }
  }

  const onDownloadCalendar = async () => {
    if (!booking || detail?.actor_scope === 'guest') return
    setActionBusy(true)
    setMsg(null)
    try {
      const kind = booking.status === 'CANCELLED' || booking.status === 'REFUNDED' ? 'cancel' : 'request'
      await downloadBookingIcs(booking.public_id, kind)
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || e?.message || 'Failed to download calendar file.')
    } finally {
      setActionBusy(false)
    }
  }

  const onOpenPay = async () => {
    if (!booking) return
    setActionBusy(true)
    setPayError(null)
    try {
      const resp = await payBookingNow(booking.public_id, guestToken || undefined)
      setPayClientSecret(resp.client_secret || '')
      setPayOpen(true)
      await loadDetail()
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || e?.message || 'Unable to start payment.')
    } finally {
      setActionBusy(false)
    }
  }

  const onSaveReschedule = async () => {
    if (!booking || !rescheduleSelected) return
    setActionBusy(true)
    setRescheduleError(null)
    try {
      await rescheduleBooking(booking.public_id, rescheduleSelected.starts_at)
      setRescheduleOpen(false)
      setRescheduleSelected(null)
      setMsg('Booking rescheduled.')
      await loadDetail()
    } catch (e: any) {
      setRescheduleError(e?.response?.data?.detail || e?.message || 'Failed to reschedule booking.')
    } finally {
      setActionBusy(false)
    }
  }

  const onOpenReview = async () => {
    if (!booking) return
    setReviewError(null)
    setReviewBody('')
    setReviewRating(5)
    setReviewOpen(true)
    try {
      const existing = await getBookingReview(booking.public_id, guestToken || undefined)
      setBookingReview(existing)
      setReviewRating(Math.max(1, Math.min(5, Number(existing.rating || 5))))
      setReviewBody(existing.body || '')
    } catch {
      setBookingReview(null)
    }
  }

  const onSubmitReview = async () => {
    if (!booking || reviewBusy) return
    setReviewBusy(true)
    setReviewError(null)
    try {
      const created = await createBookingReview(
        booking.public_id,
        { rating: Math.max(1, Math.min(5, Number(reviewRating || 5))), body: reviewBody.trim() },
        guestToken || undefined,
      )
      setBookingReview(created)
      setReviewOpen(false)
      setMsg('Thanks for your verified review.')
      await loadDetail()
    } catch (e: any) {
      setReviewError(e?.response?.data?.detail || e?.message || 'Failed to submit review.')
    } finally {
      setReviewBusy(false)
    }
  }

  const onFindNext = async () => {
    if (!rescheduleDate) return
    for (let offset = 1; offset <= 30; offset += 1) {
      const date = addDaysIso(rescheduleDate, offset)
      await loadRescheduleDay(date)
      const summary = rescheduleDaySummaries[date]
      if (summary && summary.availableCount > 0) {
        setRescheduleDate(date)
        setRescheduleMonth(monthStartIso(date))
        return
      }
    }
    setRescheduleError('No upcoming availability in the next 30 days.')
  }

  return (
    <div className="min-h-screen">
      <NavBar showSearch={false} showMobileModePill={false} />
      <main className="mx-auto max-w-5xl px-4 py-6">
        <div className="mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => {
              if (window.history.length > 1) navigate(-1)
              else navigate('/pages')
            }}
            className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-sm hover:bg-white/10"
          >
            Back
          </button>
          {hostLink ? (
            <Link to={hostLink} className="text-sm text-brand hover:underline">
              View host page
            </Link>
          ) : null}
        </div>

        {loading ? <div className="h-36 rounded-xl border border-white/10 bg-white/5" /> : null}
        {error ? <div className="rounded-xl border border-red-400/40 bg-red-500/10 p-3 text-sm text-red-100">{error}</div> : null}

        {detail && booking ? (
          <div className="space-y-4">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${statusTone}`}>{booking.status}</span>
                <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${paymentTone}`}>{booking.payment_status}</span>
              </div>
              <h1 className="text-xl font-extrabold">Booking {booking.public_id.slice(0, 8)}</h1>
              <p className="mt-1 text-sm text-white/70">Timezone: {detail.timezone}</p>
            </div>

            {msg ? <div className="rounded-xl border border-brand/40 bg-brand/10 p-3 text-sm text-brand">{msg}</div> : null}

            <div className="grid gap-4 md:grid-cols-2">
              <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <h2 className="mb-3 text-sm font-semibold text-white/80">Details</h2>
                <div className="space-y-2 text-sm">
                  <div>Service: <span className="text-white/80">{booking.service_name || 'Default appointment'}</span></div>
                  {booking.staff ? <div>Staff: <span className="text-white/80">{booking.staff.display_name}</span></div> : null}
                  <div>Starts: <span className="text-white/80">{fmtWhen(booking.starts_at, detail.timezone)}</span></div>
                  <div>Ends: <span className="text-white/80">{fmtWhen(booking.ends_at, detail.timezone)}</span></div>
                  <div>Total: <span className="text-white/80">{money(booking.amount_total_cents)}</span></div>
                  <div>Deposit: <span className="text-white/80">{money(booking.deposit_amount_cents)}</span></div>
                  <div>Platform fee: <span className="text-white/80">{money(booking.platform_fee_cents)}</span></div>
                  <div>Paid at: <span className="text-white/80">{booking.paid_at ? fmtWhen(booking.paid_at, detail.timezone) : '—'}</span></div>
                </div>
              </section>

              <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <h2 className="mb-3 text-sm font-semibold text-white/80">Host</h2>
                {host?.type === 'business' ? (
                  <div className="space-y-2 text-sm">
                    <div className="text-white">{host.name || 'Business'}</div>
                    {host.address ? <div className="text-white/70">{host.address}</div> : null}
                    {host.phone ? <div className="text-white/70">{host.phone}</div> : null}
                    {host.website ? <a href={host.website} target="_blank" rel="noreferrer" className="text-brand hover:underline">{host.website}</a> : null}
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    {host?.avatar ? <img src={host.avatar} alt={host.display_name || host.handle || 'Pro'} className="h-10 w-10 rounded-full object-cover" /> : <div className="h-10 w-10 rounded-full bg-white/10" />}
                    <div>
                      <div className="text-white">{host?.display_name || host?.handle || 'Pro'}</div>
                      {host?.handle ? <div className="text-xs text-white/60">@{host.handle}</div> : null}
                    </div>
                  </div>
                )}
              </section>
            </div>

            <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <h2 className="mb-3 text-sm font-semibold text-white/80">Actions</h2>
              <div className="mb-3 text-xs text-white/60">
                Customer policies: cancel cutoff {policy?.cancel_cutoff_minutes ?? 0}m, reschedule cutoff {policy?.reschedule_cutoff_minutes ?? 0}m
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={actionBusy || !flags?.can_reschedule}
                  onClick={() => setRescheduleOpen(true)}
                  className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm hover:bg-white/10 disabled:opacity-50"
                >
                  Reschedule
                </button>
                <button
                  type="button"
                  disabled={actionBusy || !flags?.can_cancel}
                  onClick={() => void onCancel()}
                  className="rounded-lg border border-red-400/35 bg-red-500/10 px-3 py-2 text-sm text-red-100 hover:bg-red-500/20 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={actionBusy || !flags?.can_refund}
                  onClick={() => void onRefund()}
                  className="rounded-lg border border-amber-400/35 bg-amber-500/10 px-3 py-2 text-sm text-amber-100 hover:bg-amber-500/20 disabled:opacity-50"
                >
                  Refund
                </button>
                <button
                  type="button"
                  disabled={actionBusy || !flags?.can_resend_email}
                  onClick={() => void onResend()}
                  className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm hover:bg-white/10 disabled:opacity-50"
                >
                  Resend email
                </button>
                {detail?.actor_scope !== 'guest' ? (
                  <button
                    type="button"
                    disabled={actionBusy}
                    onClick={() => void onDownloadCalendar()}
                    className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm hover:bg-white/10 disabled:opacity-50"
                  >
                    {booking.status === 'CANCELLED' || booking.status === 'REFUNDED' ? 'Download cancellation' : 'Add to calendar'}
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={actionBusy || !flags?.can_pay_now}
                  onClick={() => void onOpenPay()}
                  className="rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                >
                  Pay now
                </button>
                <button
                  type="button"
                  disabled={actionBusy || !flags?.can_review}
                  onClick={() => void onOpenReview()}
                  className="rounded-lg border border-emerald-400/35 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-100 hover:bg-emerald-500/20 disabled:opacity-50"
                >
                  Leave verified review
                </button>
              </div>
              {flags?.review_submitted ? (
                <div className="mt-3 text-xs text-emerald-200/90">Verified review already submitted for this booking.</div>
              ) : null}
            </section>
          </div>
        ) : null}
      </main>

      {rescheduleOpen ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#111] p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Reschedule booking</h3>
              <button type="button" onClick={() => setRescheduleOpen(false)} className="text-sm text-white/70 hover:text-white">Close</button>
            </div>
            <BookingCalendar
              selectedDate={rescheduleDate}
              visibleMonth={rescheduleMonth || monthStartIso(rescheduleDate || todayIsoDate())}
              onVisibleMonthChange={setRescheduleMonth}
              onSelectDate={setRescheduleDate}
              daySummaries={rescheduleDaySummaries}
            />
            <div className="mt-3 flex items-center justify-between">
              <div className="text-xs text-white/60">Date: {rescheduleDate || 'Select a day'}</div>
              <button
                type="button"
                onClick={() => void onFindNext()}
                className="rounded border border-white/15 bg-white/5 px-3 py-1.5 text-xs hover:bg-white/10"
              >
                Next available
              </button>
            </div>
            {rescheduleError ? <div className="mt-2 text-sm text-red-300">{rescheduleError}</div> : null}
            {rescheduleLoading ? <div className="mt-2 text-sm text-white/70">Loading slots...</div> : null}
            {!rescheduleLoading ? (
              <div className="mt-3">
                <BookingDaySlots
                  slots={rescheduleSlots}
                  onPick={(slot) => setRescheduleSelected(slot)}
                />
              </div>
            ) : null}
            {rescheduleSelected ? (
              <div className="mt-3 rounded-lg border border-brand/30 bg-brand/10 px-3 py-2 text-xs text-brand">
                Selected: {fmtWhen(rescheduleSelected.starts_at, detail?.timezone)}
              </div>
            ) : null}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRescheduleOpen(false)}
                className="rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={actionBusy || !rescheduleSelected}
                onClick={() => void onSaveReschedule()}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
              >
                Save reschedule
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {payOpen ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-[#111] p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Complete payment</h3>
              <button type="button" onClick={() => setPayOpen(false)} className="text-sm text-white/70 hover:text-white">Close</button>
            </div>
            {payError ? <div className="mb-2 text-sm text-red-300">{payError}</div> : null}
            {!payClientSecret ? <div className="text-sm text-white/70">Unable to initialize payment.</div> : null}
            {payClientSecret && stripePromise ? (
              <Elements stripe={stripePromise} options={{ clientSecret: payClientSecret, appearance: { theme: 'night' as const } }}>
                <BookingPaymentConfirm
                  busy={payBusy}
                  setBusy={setPayBusy}
                  onError={(message) => setPayError(message || null)}
                  onSuccess={() => {
                    setPayOpen(false)
                    setMsg('Payment submitted. Status will update shortly.')
                    void loadDetail()
                  }}
                />
              </Elements>
            ) : null}
          </div>
        </div>
      ) : null}

      {reviewOpen ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#111] p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Leave a verified review</h3>
              <button type="button" onClick={() => setReviewOpen(false)} className="text-sm text-white/70 hover:text-white">Close</button>
            </div>
            {reviewError ? <div className="mb-2 text-sm text-red-300">{reviewError}</div> : null}
            <div className="mb-3 flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setReviewRating(star)}
                  className={`text-2xl ${reviewRating >= star ? 'text-yellow-400' : 'text-white/35'}`}
                >
                  ★
                </button>
              ))}
            </div>
            <textarea
              value={reviewBody}
              onChange={(e) => setReviewBody(e.target.value)}
              placeholder="Share your experience (optional)"
              className="min-h-[110px] w-full rounded-xl border border-white/10 bg-black/30 p-3 text-sm text-white outline-none focus:ring-2 focus:ring-brand"
            />
            {bookingReview ? (
              <div className="mt-2 text-xs text-emerald-200/90">Your existing verified review is loaded. Saving will not create a duplicate.</div>
            ) : null}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setReviewOpen(false)}
                className="rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void onSubmitReview()}
                disabled={reviewBusy}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
              >
                {reviewBusy ? 'Submitting...' : 'Submit review'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
